import { useMemo } from 'react'

import { DeepMap, FieldValues } from 'react-hook-form'

import {
  useDeviceEditQuery,
  useFloorsQuery,
  useUpdateAlarmMutation,
  useUpdateDeviceMutation,
} from '@/graphql/generated/hooks'
import { UpdateAlarmInput } from '@/graphql/generated/schemas'

import { UpdateDeviceInputsI } from '../../types/types'
import { getFloorOptions } from '../../utils/utils'
import { isAlarmFieldsDirty, isDeviceFieldsDirty } from '../utils/utils'

interface UseEditAlarmIProps {
  deviceId: string
}

/**
 * Custom hook for managing alarm device editing functionality
 *
 * @param deviceId - The ID of the device to edit (device.id === alarm.id)
 * @returns Object containing alarm data, loading states, and update functions
 */
export const useEditAlarm = ({ deviceId }: UseEditAlarmIProps) => {
  const { data: floorsData, loading: isFloorsLoading } = useFloorsQuery({
    fetchPolicy: 'network-only',
  })

  const [updateDevice, { loading: isUpdateDeviceLoading }] =
    useUpdateDeviceMutation()

  const [updateAlarmDevice, { loading: isUpdateAlarmLoading }] =
    useUpdateAlarmMutation()

  const { data: alarmData, loading: isAlarmLoading } = useDeviceEditQuery({
    variables: {
      id: deviceId,
    },
    fetchPolicy: 'network-only',
  })

  const floorsOptions = useMemo(() => getFloorOptions(floorsData), [floorsData])

  const updateDeviceFields = async (formData: UpdateDeviceInputsI) => {
    await updateDevice({
      variables: {
        input: formData,
      },
      refetchQueries: ['Devices', 'DeviceEdit'],
    })
  }

  const updateAlarm = async (
    formData: UpdateDeviceInputsI,
    alarmInputData: UpdateAlarmInput,
    dirtyFields: DeepMap<FieldValues, true>
  ) => {
    // Only update device fields if they were modified
    if (isDeviceFieldsDirty(dirtyFields)) {
      await updateDeviceFields(formData)
    }

    // Only update alarm fields if they were modified
    if (isAlarmFieldsDirty(dirtyFields)) {
      await updateAlarmDevice({
        variables: {
          input: alarmInputData,
        },
        refetchQueries: ['DeviceEdit'],
      })
    }
  }

  return {
    alarmData,
    isLoading: isAlarmLoading || isFloorsLoading,
    isUpdateDeviceLoading,
    isUpdateAlarmLoading,
    floorsOptions,
    updateAlarm,
  }
}
